import { Component, computed, inject, Signal } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import {
	MDIconComponent,
	MDHeaderComponent,
	MDDialogService,
	MDContentBoxComponent,
	MDAlertService,
	MDToolTipDirective,
	IAlert
} from '@melodic-ui/components';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { SurveyDialogComponent } from '../survey-dialog/survey-dialog.component';
import { SurveyActionComponent } from '../survey-action/survey-action.component';
import { isLessonEditable } from '@lb/components/lessons/utils/lesson-helper.util';
import { USER_VIEW_TOKEN } from '../../enrollment-detail/enrollment-detail.tokens';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { buildAppUrl } from '@lb/config/utils/app-url.util';
import { IGradingTemplateSurveyDetail } from '@lb/api/core/interfaces/igrading-template.interface';
import { ISurveyResponseSummary } from '@lb/api/mylb/interfaces';
import { take } from 'rxjs';

@Component({
	selector: 'lb-lesson-detail-survey',
	templateUrl: './lesson-detail-survey.component.html',
	styleUrl: './lesson-detail-survey.component.scss',
	imports: [MDIconComponent, MDHeaderComponent, MDContentBoxComponent, MDToolTipDirective, SurveyActionComponent],
	standalone: true
})
export class LessonDetailSurveyComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly dialogService = inject(MDDialogService);
	private readonly alertService = inject(MDAlertService);
	private readonly clipboard = inject(Clipboard);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);
	private readonly userViewToken = inject(USER_VIEW_TOKEN, { optional: true });

	/**
	 * Whose actions to offer. The enrollment and lesson routes supply the token; a host that does not
	 * falls back to the signed-in role (instructors and admins → 'instructor'), and only then to
	 * 'learner', which fails closed — an unrecognised host cannot submit on the instructor's behalf.
	 */
	private readonly userView = computed<'learner' | 'instructor'>(() => this.userViewToken ?? this.state.experience() ?? 'learner');

	/**
	 * Who the viewer is allowed to answer for. A survey is only submittable by its intended respondent:
	 * the learner answers respondentType 1, the instructor answers respondentType 2, and each sees the
	 * other's row as status only. Admin routes supply 'instructor', so an admin gets the instructor's
	 * actions rather than the learner's.
	 */
	protected readonly isLearnerView = computed(() => this.userView() === 'learner');
	protected readonly isInstructorView = computed(() => this.userView() === 'instructor');
	/**
	 * Instructor surveys are answered by the instructor who taught the lesson. The enrollment's current
	 * instructor viewing a predecessor's or substitute's lesson sees the survey but can't submit it.
	 */
	protected readonly canSubmitInstructorSurvey = computed(() => this.isInstructorView() && isLessonEditable(this.state.lesson()));

	/**
	 * Absolute link to the learner's own view of this lesson, so an instructor can send the learner
	 * somewhere their survey is actually reachable — the instructor's address bar points at the
	 * instructor app, which the learner cannot open. Null when there is nothing to share: the learner's
	 * own view, no learner survey, no learner survey open yet (the link would be a dead end), or no
	 * configured learner origin.
	 */
	readonly learnerLessonLink: Signal<string | null> = computed(() => {
		if (this.userView() !== 'instructor') return null;

		const learnerSurveys = this.state.activeLearnerSurveys();
		if (learnerSurveys.length === 0) return null;
		if (!learnerSurveys.some((survey) => this.state.surveyAvailable(survey))) return null;

		const lesson = this.state.lesson();
		if (!lesson?.enrollmentID || !lesson?.lessonID) return null;

		const learnerAppUrl = this.appSettings.learnerAppUrl;
		if (!learnerAppUrl) return null;

		return buildAppUrl(learnerAppUrl, `enrollments/${lesson.enrollmentID}/lessons/${lesson.lessonID}`);
	});

	copyLearnerLink(): void {
		const link = this.learnerLessonLink();
		if (!link) return;

		const copied = this.clipboard.copy(link);

		this.alertService.addAlert({
			type: copied ? 'success' : 'error',
			title: copied ? 'Link Copied' : 'Unable to Copy Link',
			message: copied ? "The learner's link to this lesson has been copied to your clipboard." : `Copy this link manually: ${link}`,
			duration: copied ? 4000 : 10000
		} as IAlert);
	}

	openSurvey(survey: IGradingTemplateSurveyDetail, readonly: boolean): void {
		const lesson = this.state.lesson();
		if (!lesson) return;

		// Answering is only legitimate inside the survey's window. lb-survey-action already withholds the
		// button, but the guard lives here too so the rule doesn't rest on one template branch — the submit
		// endpoint enforces it as well. Read-only opens are always allowed: an answered survey stays
		// readable even if a reschedule pushes its lesson back past the opening date.
		if (!readonly && !this.state.surveyAvailable(survey)) return;

		const summary = this.state.getSurveyResponseSummary(survey.surveyID);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const ref = this.dialogService.open(SurveyDialogComponent as any, {
			survey,
			lessonID: lesson.lessonID,
			semesterID: lesson.semesterID,
			enrollmentID: lesson.enrollmentID,
			responseID: summary?.surveyResponseID ?? null,
			readonly
		});

		if (!readonly) {
			ref.afterClosed.pipe(take(1)).subscribe((result) => {
				if (result) {
					this.state.upsertSurveyResponseSummary(result as ISurveyResponseSummary);
				}
			});
		}
	}
}
